// AN-1 — the bridge between three identifiers that never coincide, and
// the reason it has to be frozen into a file instead of computed live.
//
//   • the JL slug        `cable-curl`   — what the app searches by
//   • the GymVisual name `Cable Curl`   — the CSV's `gymvisual_product`
//   • the ASSET ID       `01561301`     — the name of the zip, and it
//                                         does NOT appear in the CSV
//
// The only thing joining the last two is the NAME, which GymVisual bakes
// into the GIF filenames inside each zip. ⛔ That is a heuristic, not a
// link: Antenore buys more animations every month, and the first wave
// whose product is spelled `Air bike` where the file says `air-bike-m`
// breaks it. So the join is computed once, reviewed, and committed — and
// the code that ships reads the committed manifest and never guesses.
//
// ⭐ Which makes the UNRESOLVED half load-bearing. A row that does not
// join is emitted with a reason, never silently dropped and never
// resolved by "close enough": a wrong assetId shows the athlete another
// exercise's movement, which is worse than showing none.
//
// ⭐ And `overrides` is what makes the script REPEATABLE rather than a
// one-shot. Without it, the four name-variant mismatches that a human
// resolves this month are re-broken by next month's rerun — or worse,
// the rerun overwrites a hand-edited manifest and nobody notices. The
// hand decision has to live somewhere the generator reads, not somewhere
// it clobbers. Shape borrowed from the two siblings that already do this
// in production: catalogValidation's exceptions and featuresWebDebt's
// baseline, `staleOverrides` included — an override the heuristic no
// longer needs is reported so the file SHRINKS instead of rotting.
//
// Pure: no IO, and — measured, not assumed — no runtime imports. Both are
// load-bearing. apps/api/scripts/animationManifest.ts runs under bare
// Node's native type-stripping, which cannot resolve the `.js`-suffixed
// sibling imports this package is authored with; the probe fails with
// ERR_MODULE_NOT_FOUND before a line runs. It is the trap already
// documented at apps/api/scripts/liftPool.ts:12, and it is why
// `normalizeKey` below is local instead of imported.

/** One row of the GymVisual purchase CSV, as authored. */
export interface AnimationCsvRow {
  /** Catalog exercise name as Antenore matched it, e.g. `Cable Curl`. */
  jackedlogs_exercise: string;
  /** GymVisual's product name, e.g. `Cable Curl (female)`. */
  gymvisual_product: string;
  /** GymVisual's product id, e.g. `5541`. Traceability only — not the asset id. */
  gymvisual_product_id: string;
  /** `female` | `male` | `default (male)`. Unused today: no section of next/exercise-animations.md covers it. */
  model: string;
}

/** One purchased zip, reduced to what the join needs. */
export interface AnimationAsset {
  /** The zip's basename, e.g. `01561301`. */
  assetId: string;
  /**
   * The GIF filename shared by every resolution, minus `_<res>.gif` —
   * e.g. `01561301-Cable-Curl_Upper-Arms`. Carried into the manifest
   * because an assetId alone cannot build a URL, and AN-3 will need one.
   */
  file: string;
  /** Pixel widths present in the zip, ascending. The GIFs are square. */
  resolutions: number[];
}

/**
 * A join a human made by hand, because the heuristic could not.
 *
 * ⛔ Authored, never generated: the script emits the unresolved list and
 * stops there. Deciding which zip is `Pallof Press` is Antenore's call.
 */
export interface AnimationOverride {
  /** Matches `jackedlogs_exercise` of the CSV row this fixes, verbatim. */
  exercise: string;
  /** Supplies the catalog slug when the name does not slugify to one. */
  slug?: string;
  /** Supplies the zip when the product name matches none, or too many. */
  assetId?: string;
  /** Why, in a human's words. Read when the override later turns stale. */
  reason: string;
}

/** What the shipped manifest says about one exercise. */
export interface AnimationManifestEntry {
  assetId: string;
  file: string;
  gymvisualProductId: number;
  resolutions: number[];
}

/** slug → entry. Slug-sorted, so a new wave lands as a readable git diff. */
export type AnimationManifest = Record<string, AnimationManifestEntry>;

/** Why one row, one zip, or one override did not make it. */
export type AnimationRejectReason =
  /** `jackedlogs_exercise` does not slugify to any slug in the catalog. */
  | "unknown-slug"
  /** No zip carries this `gymvisual_product` name. */
  | "no-asset"
  /** More than one zip carries it — resolving it would be a coin toss. */
  | "ambiguous-asset"
  /** Two CSV rows claim the same catalog exercise. */
  | "duplicate-slug"
  /** Two CSV rows claim the same zip. */
  | "duplicate-asset"
  /** The zip's GIFs disagree on the stem, or one has no `_<res>.gif`. */
  | "malformed-asset"
  /** A zip on disk that no CSV row claims — bought but never mapped. */
  | "orphan-asset"
  /** `gymvisual_product_id` is not a positive integer. */
  | "bad-product-id"
  /** An override naming an exercise, slug, or zip that does not exist. */
  | "broken-override";

export interface AnimationReject {
  reason: AnimationRejectReason;
  /** `jackedlogs_exercise`, or the assetId for `orphan-asset`. */
  subject: string;
  /** Everything needed to resolve it without re-deriving the state. */
  detail: string;
  /**
   * Zips or slugs whose name is close to the unmatched one — a hint for
   * the human. ⛔ Never acted on: a suggestion the script could not be
   * wrong about is one it should have applied itself.
   */
  candidates?: string[];
}

export interface AnimationManifestResult {
  manifest: AnimationManifest;
  /** Sorted by reason then subject, so the committed report is stable. */
  rejects: AnimationReject[];
  /**
   * Overrides that are no longer doing any work — the row now resolves
   * the same way without them. Reported, never auto-pruned: deleting a
   * human's reviewed decision is not a script's call.
   */
  staleOverrides: { exercise: string; why: string }[];
}

/**
 * Fold a human string to a comparison key: `Cable Curl (female)` →
 * `cable-curl-female`.
 *
 * ⚠️ Deliberately NOT one of the two slugifiers this repo already has,
 * and this is a registered duplication (CN-ANIM-NORMALIZE in
 * next/consolidation.md), not an unnoticed one:
 *   • `apps/web/src/exercises/slugify.ts:6` had near-identical regexes,
 *     but lived in the legacy web (removed with RW-14, readable at 7ae8e35e).
 *   • `foldDiacritics` (schemas/tag.ts:116) is the CLOSEST sibling — the
 *     fold step below is byte-for-byte its body. It lives in this very
 *     package and would need no lift; only the bare-Node constraint keeps
 *     it out. ⚠️ Named here because the first version of this note listed
 *     only the two slugifiers and missed the function it actually copies.
 *   • `slugifyTagValue` (schemas/tag.ts:122) DELETES accented letters
 *     rather than folding them (`Genève` → `genve`), and its behaviour
 *     is frozen for compatibility with values the importer and the
 *     catalog editor have already written — CN-slugify-diacritics.
 * Above all, importing ANY runtime sibling breaks the bare-Node CLI, as
 * measured in this file's header.
 *
 * ⛔ It is a COMPARISON key, not a slug generator: nothing is persisted
 * from it, so it carries no length cap (slugify's `.slice(0, 64)` is a
 * persistence rule and would only truncate a comparison). It folds
 * diacritics, lowercases, and collapses runs of non-alphanumerics to one
 * dash — and stops there. It does NOT strip `(female)`, `-m`, or any
 * other variant marker, and that omission is the whole point: stripping
 * them would quietly pair a male product with a female asset. Those rows
 * are MEANT to land in the unresolved list and be decided by a human.
 */
export const normalizeKey = (raw: string): string =>
  raw
    .normalize("NFD")
    // U+0300..U+036F — the combining-diacritics block NFD splits off.
    // Escaped, not literal: a combining mark in source is invisible in a
    // diff (same reasoning as foldDiacritics in schemas/tag.ts).
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/**
 * Split a GIF filename into stem and pixel width:
 * `01561301-Cable-Curl_Upper-Arms_720.gif` → `{ stem, resolution: 720 }`.
 */
const splitGifName = (
  name: string,
): { stem: string; resolution: number } | null => {
  const match = /^(.+)_(\d+)\.gif$/.exec(name);
  const [, stem, width] = match ?? [];
  return stem !== undefined && width !== undefined
    ? { stem, resolution: Number(width) }
    : null;
};

/**
 * Reduce one zip's entry names to an asset, or say why it cannot be one.
 * Lives here rather than in the CLI so the malformed cases are covered by
 * the unit test instead of only by a wave that happens to be broken.
 *
 * @param assetId basename of the zip
 * @param entryNames GIF filenames inside it (basenames, any order)
 */
export const readAsset = (
  assetId: string,
  entryNames: string[],
): { asset: AnimationAsset } | { reject: AnimationReject } => {
  const malformed = (detail: string): { reject: AnimationReject } => ({
    reject: { reason: "malformed-asset", subject: assetId, detail },
  });
  if (entryNames.length === 0) return malformed("the zip is empty");

  let stem: string | null = null;
  const resolutions: number[] = [];
  for (const name of entryNames) {
    const split = splitGifName(name);
    if (!split) return malformed(`"${name}" does not end in _<width>.gif`);
    if (stem === null) stem = split.stem;
    else if (stem !== split.stem) {
      return malformed(
        `its GIFs disagree on the name: "${stem}" vs "${split.stem}"`,
      );
    }
    resolutions.push(split.resolution);
  }
  if (stem === null) return malformed("the zip is empty");
  if (!stem.startsWith(`${assetId}-`)) {
    return malformed(`"${stem}" is not prefixed with the zip name`);
  }
  return {
    asset: {
      assetId,
      file: stem,
      resolutions: [...resolutions].sort((a, b) => a - b),
    },
  };
};

/**
 * The GymVisual product name a zip carries, taken from its filename:
 * `01561301-Cable-Curl_Upper-Arms` → `Cable-Curl`. The segment after the
 * asset id and before the first `_`, which starts the body part.
 */
const productNameOf = (asset: AnimationAsset): string =>
  asset.file.slice(asset.assetId.length + 1).split("_")[0] ?? "";

/** Keys containing (or contained by) the unmatched one — a hint only. */
const nearby = (key: string, pool: Iterable<string>): string[] =>
  [...pool].filter((o) => o !== key && (o.includes(key) || key.includes(o))).sort();

/** What one CSV row resolved to, or the reason it did not. */
type RowOutcome =
  | { slug: string; asset: AnimationAsset; productId: number }
  | { reject: AnimationReject };

const isReject = (o: RowOutcome): o is { reject: AnimationReject } =>
  "reject" in o;

/**
 * Join the CSV, the zips on disk, the catalog, and the hand-authored
 * overrides into a manifest plus an itemised list of what did not join.
 *
 * @param csvRows rows of the GymVisual purchase CSV, as authored
 * @param assets zips already reduced by `readAsset`
 * @param catalogSlugs every slug in the exercise catalog
 * @param overrides hand-made joins, from tooling/animations/overrides.json
 */
export const buildAnimationManifest = (
  csvRows: AnimationCsvRow[],
  assets: AnimationAsset[],
  catalogSlugs: Iterable<string>,
  overrides: AnimationOverride[] = [],
): AnimationManifestResult => {
  const slugs = new Set(catalogSlugs);
  const assetsById = new Map(assets.map((a) => [a.assetId, a]));

  // Product name → the zips carrying it. A list, not a value: a collision
  // has to surface as `ambiguous-asset`, never a last-write-wins.
  const byProductName = new Map<string, AnimationAsset[]>();
  for (const asset of assets) {
    const key = normalizeKey(productNameOf(asset));
    const bucket = byProductName.get(key);
    if (bucket) bucket.push(asset);
    else byProductName.set(key, [asset]);
  }

  const overrideFor = new Map<string, AnimationOverride>();
  const duplicateOverrides: AnimationReject[] = [];
  for (const override of overrides) {
    const key = override.exercise.trim();
    const first = overrideFor.get(key);
    if (first) {
      // Hand-authored file, so a copy-pasted entry is the likely mistake.
      // Silently keeping the last one would be the module guessing, which
      // is the one thing it must never do.
      duplicateOverrides.push({
        reason: "broken-override",
        subject: key,
        detail: `two overrides name this exercise ("${first.reason}" and "${override.reason}") — keep one`,
      });
      continue;
    }
    overrideFor.set(key, override);
  }

  /**
   * Resolve one row's two legs — slug and zip. Called twice for a row
   * that has an override: once with it, once without, so a no-longer-
   * needed override can be reported instead of silently living forever.
   */
  const resolveRow = (
    row: AnimationCsvRow,
    override: AnimationOverride | undefined,
  ): RowOutcome => {
    const exercise = row.jackedlogs_exercise.trim();
    const product = row.gymvisual_product.trim();
    const via = override ? ` (via the override "${override.reason}")` : "";

    const slug = override?.slug ?? normalizeKey(exercise);
    if (!slugs.has(slug)) {
      return {
        reject: {
          reason: override?.slug ? "broken-override" : "unknown-slug",
          subject: exercise,
          detail: `"${exercise}" resolves to the slug "${slug}"${via}, which is not in the exercise catalog: add the exercise, or correct the name`,
          candidates: nearby(slug, slugs),
        },
      };
    }

    // Checked before the conversion at the write site: Number("") is 0 and
    // Number("abc") is NaN, and either would land in the manifest silently.
    const rawId = row.gymvisual_product_id.trim();
    const productId = Number(rawId);
    if (!/^\d+$/.test(rawId) || !Number.isSafeInteger(productId) || productId <= 0) {
      return {
        reject: {
          reason: "bad-product-id",
          subject: exercise,
          detail: `gymvisual_product_id is "${rawId}" (expected a positive integer)`,
        },
      };
    }

    if (override?.assetId) {
      const asset = assetsById.get(override.assetId);
      return asset
        ? { slug, asset, productId }
        : {
            reject: {
              reason: "broken-override",
              subject: exercise,
              detail: `the override names zip ${override.assetId}${via}, which is not on disk`,
            },
          };
    }

    const matches = byProductName.get(normalizeKey(product)) ?? [];
    if (matches.length === 0) {
      return {
        reject: {
          reason: "no-asset",
          subject: exercise,
          detail: `no purchased zip is named after "${product}"`,
          candidates: nearby(normalizeKey(product), byProductName.keys()),
        },
      };
    }
    const [only, ...rest] = matches;
    if (rest.length > 0 || !only) {
      return {
        reject: {
          reason: "ambiguous-asset",
          subject: exercise,
          detail: `"${product}" matches ${matches.length} zips — pick one in overrides.json`,
          candidates: matches.map((a) => a.assetId).sort(),
        },
      };
    }
    return { slug, asset: only, productId };
  };

  const manifest: AnimationManifest = {};
  const rejects: AnimationReject[] = [...duplicateOverrides];
  const staleOverrides: { exercise: string; why: string }[] = [];
  const claimedSlugs = new Map<string, string>();
  const claimedAssets = new Map<string, string>();
  const seenExercises = new Set<string>();

  for (const row of csvRows) {
    const exercise = row.jackedlogs_exercise.trim();
    seenExercises.add(exercise);
    const override = overrideFor.get(exercise);
    const outcome = resolveRow(row, override);

    if (override) {
      const withoutIt = resolveRow(row, undefined);
      if (
        !isReject(outcome) &&
        !isReject(withoutIt) &&
        withoutIt.slug === outcome.slug &&
        withoutIt.asset.assetId === outcome.asset.assetId
      ) {
        staleOverrides.push({
          exercise,
          why: "the name now matches on its own — the override changes nothing",
        });
      }
    }

    if (isReject(outcome)) {
      rejects.push(outcome.reject);
      continue;
    }
    const { slug, asset, productId } = outcome;

    const slugOwner = claimedSlugs.get(slug);
    if (slugOwner !== undefined) {
      rejects.push({
        reason: "duplicate-slug",
        subject: exercise,
        detail: `"${slug}" is already claimed by the row "${slugOwner}"`,
      });
      continue;
    }
    const assetOwner = claimedAssets.get(asset.assetId);
    if (assetOwner !== undefined) {
      rejects.push({
        reason: "duplicate-asset",
        subject: exercise,
        detail: `zip ${asset.assetId} is already claimed by the row "${assetOwner}"`,
      });
      continue;
    }

    claimedSlugs.set(slug, exercise);
    claimedAssets.set(asset.assetId, exercise);
    manifest[slug] = {
      assetId: asset.assetId,
      file: asset.file,
      gymvisualProductId: productId,
      resolutions: asset.resolutions,
    };
  }

  // A zip nobody NAMED is money spent that never reaches an athlete — the
  // failure this module exists to make visible.
  //
  // ⛔ "Nobody named it" is measured against the CSV and the overrides, NOT
  // against `claimedAssets`. Those are different questions, and conflating
  // them made the report lie: a zip whose row was rejected for some other
  // reason (an ambiguity, an unknown slug) is unclaimed but very much
  // named, and it was being reported a second time under a detail that
  // said the opposite. One real problem became three rejects, two of them
  // false. Found by review, verified by running it.
  const namedProducts = new Set(
    csvRows.map((r) => normalizeKey(r.gymvisual_product.trim())),
  );
  const namedAssetIds = new Set(
    overrides.flatMap((o) => (o.assetId ? [o.assetId] : [])),
  );
  for (const asset of assets) {
    if (claimedAssets.has(asset.assetId)) continue;
    if (
      namedProducts.has(normalizeKey(productNameOf(asset))) ||
      namedAssetIds.has(asset.assetId)
    ) {
      // Named but unused: the reason is already filed against its row.
      continue;
    }
    rejects.push({
      reason: "orphan-asset",
      subject: asset.assetId,
      detail: `purchased "${productNameOf(asset)}" is not named by any CSV row`,
    });
  }

  // An override for a row that has left the CSV is the other way a
  // reviewed decision rots — it stops applying without ever being wrong.
  for (const override of overrides) {
    if (seenExercises.has(override.exercise.trim())) continue;
    staleOverrides.push({
      exercise: override.exercise,
      why: "no CSV row carries this exercise any more",
    });
  }

  const sortedManifest: AnimationManifest = {};
  // Sorted by code unit, not locale, so the committed file is byte-stable
  // across machines — the diff is the review surface for a new wave.
  for (const [slug, entry] of Object.entries(manifest).sort(([a], [b]) =>
    a < b ? -1 : a > b ? 1 : 0,
  )) {
    sortedManifest[slug] = entry;
  }
  rejects.sort(
    (a, b) =>
      a.reason.localeCompare(b.reason) || a.subject.localeCompare(b.subject),
  );
  staleOverrides.sort((a, b) => a.exercise.localeCompare(b.exercise));
  return { manifest: sortedManifest, rejects, staleOverrides };
};

/** Human report — what Antenore reads after a new wave lands. */
export const formatAnimationRejects = (
  result: AnimationManifestResult,
): string => {
  const lines = [
    `${Object.keys(result.manifest).length} exercises mapped, ${result.rejects.length} unresolved.`,
  ];
  let lastReason = "";
  for (const reject of result.rejects) {
    if (reject.reason !== lastReason) {
      lines.push("", `── ${reject.reason}`);
      lastReason = reject.reason;
    }
    lines.push(`   ${reject.subject}: ${reject.detail}`);
    if (reject.candidates?.length) {
      lines.push(`      candidates: ${reject.candidates.join(", ")}`);
    }
  }
  if (result.staleOverrides.length > 0) {
    lines.push("", "── overrides that no longer do anything (prune them)");
    for (const stale of result.staleOverrides) {
      lines.push(`   ${stale.exercise}: ${stale.why}`);
    }
  }
  return lines.join("\n");
};
